import { BuscaCEP } from '@/modules/structure/endereco/correios.service';
import { CandidatoService, CreateCandidatoDto } from './candidato.service';
import { CursoService } from '@/modules/structure/curso/curso.service';
import { ProcessoSeletivoService, ReadProcessoSeletivoDto } from '../processo-seletivo/processo-seletivo.service';
import { readComarcaPublic } from '@/modules/structure/comarca/comarca.service';
import { readDistritoPublic } from '@/modules/structure/distrito/distrito.service';
import { InstituicaoEnsinoService } from '@/modules/structure/instituicoes/instituicao.service';
import { MunicipioService, MunicipioIBGE } from '@/modules/structure/municipio/municipio.service';
import { ReadComarcaDto, ReadDistritoDto, ReadInstituicaoEnsinoDto } from '@/api.types';
import { SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useEffect, useState, useRef, MutableRefObject } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import FormularioInscricao from './FormularioInscricao';
import { Endpoints } from '@/shared/constants/endpoints';
import { API_URL } from '@/shared/constants/strings';
import axios from 'axios';

const logoDefensoria = '/img/logo_branco.png';
const candidatoService = new CandidatoService();
const processoSeletivoService = new ProcessoSeletivoService();
const cursoService = new CursoService();
const instituicaoEnsinoService = new InstituicaoEnsinoService();
const municipioService = new MunicipioService();

export default function InscricaoCandidatoPage() {
  const { register, handleSubmit, setValue, watch, formState: { errors }, trigger } = useForm<CreateCandidatoDto>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    shouldFocusError: false, // Desabilitar foco automático - usamos mensagens de toast para informar erros
    criteriaMode: 'all', // Validar todos os campos, não apenas o primeiro erro
  });
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const processoSeletivoId = params.id ? Number(params.id) : 0;
  const candidatoId = searchParams.get('candidatoId') ? Number(searchParams.get('candidatoId')) : null;

  // Refs para os campos de número (para foco automático)
  const telefoneNumeroRef = useRef<HTMLInputElement | null>(null) as MutableRefObject<HTMLInputElement | null>;
  const celularNumeroRef = useRef<HTMLInputElement | null>(null) as MutableRefObject<HTMLInputElement | null>;

  const [processoSeletivo, setProcessoSeletivo] = useState<ReadProcessoSeletivoDto | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCEP, setIsLoadingCEP] = useState(false);
  const [, setCursos] = useState<Array<{ id: number; nome: string }>>([]);
  const [comarcas, setComarcas] = useState<ReadComarcaDto[]>([]);
  const [distritos, setDistritos] = useState<ReadDistritoDto[]>([]);
  const [instituicoesEnsino, setInstituicoesEnsino] = useState<ReadInstituicaoEnsinoDto[]>([]);
  const [, setMunicipios] = useState<MunicipioIBGE[]>([]);
  const [municipiosDisponiveis, setMunicipiosDisponiveis] = useState<MunicipioIBGE[]>([]);
  const [municipioCandidato, setMunicipioCandidato] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const cursoPretendidoId = watch('cursoPretendidoId');
  const comarcaPretendidaId = watch('comarcaPretendidaId');
  const pessoaComDeficiencia = watch('pessoaComDeficiencia');
  const optaVagasPcd = watch('optaVagasPcd');
  const declaraIndigena = watch('declaraIndigena');
  const raca = watch('raca');
  const instituicaoEnsinoId = watch('instituicaoEnsinoId');
  const instituicaoEnsinoIdStr = String(instituicaoEnsinoId || '');

  useEffect(() => {
    if (processoSeletivoId > 0) {
      // Carregar processo seletivo usando endpoint público (sem autenticação)
      processoSeletivoService.findOnePublic(processoSeletivoId)
        .then((result) => {
          setProcessoSeletivo(result);
          setValue('processoSeletivoId', processoSeletivoId);

          // Carregar cursos do processo seletivo
          if (result.cursos && result.cursos.length > 0) {
            setCursos(result.cursos);
          }

          // Extrair municípios únicos das vagas do processo seletivo
          if (result.vagas && result.vagas.length > 0) {
            const municipiosUnicos = new Set<string>();
            result.vagas.forEach(vaga => {
              if (vaga.municipio) {
                municipiosUnicos.add(vaga.municipio);
              }
            });

            // Filtrar municípios do IBGE para mostrar apenas os disponíveis no processo
            // Carregar todos os municípios primeiro (se ainda não foram carregados)
            municipioService.getMunicipiosSergipe()
              .then((todosMunicipios) => {
                setMunicipios(todosMunicipios);
                const municipiosFiltrados = todosMunicipios.filter(m =>
                  municipiosUnicos.has(m.nome)
                );
                setMunicipiosDisponiveis(municipiosFiltrados);
              })
              .catch((error) => {
                console.error('Falha ao carregar municípios:', error);
              });
          } else {
            // Se não houver vagas, não há municípios disponíveis
            setMunicipiosDisponiveis([]);
          }
        })
        .catch((error) => {
          // Se o processo não está aberto ou não está disponível, redirecionar para página de não disponível
          const errorMessage = String(error).toLowerCase();
          if (errorMessage.includes('não está aberto') ||
            errorMessage.includes('não disponível') ||
            errorMessage.includes('não encontrado') ||
            errorMessage.includes('não está aberto para inscrições')) {
            navigate(`/processo-seletivo/nao-disponivel/${processoSeletivoId}`);
          } else {
            navigate(`/processo-seletivo/nao-disponivel/${processoSeletivoId}`);
          }
        });
    }

    readComarcaPublic({ skip: 0, take: 9999 })
      .then((result) => {
        setComarcas(result[0]);
      })
      .catch((error) => {
        toast.error('Falha ao carregar comarcas: ' + error);
      });

    municipioService.getMunicipiosSergipe()
      .then((result) => {
        setMunicipios(result);
      })
      .catch((error) => {
        console.error('Falha ao carregar municípios:', error);
      });

    instituicaoEnsinoService.EnumeratePublic()
      .then((result) => {
        setInstituicoesEnsino(result);
      })
      .catch((error) => {
        console.error('Falha ao carregar instituições de ensino:', error);
      });
  }, [processoSeletivoId, navigate, setValue]);


  useEffect(() => {
    if (candidatoId && processoSeletivoId > 0) {
      candidatoService.findOnePublic(candidatoId)
        .then((candidato) => {
          if (candidato.pessoaNome) {
            setValue('nome', candidato.pessoaNome);
          }
          if (candidato.pessoaCpf) setValue('cpf', candidato.pessoaCpf);

          if ((candidato as any).dataNascimento) {
            const raw = (candidato as any).dataNascimento as string;
            if (typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}/.test(raw)) {
              setValue('dataNascimento', raw.slice(0, 10));
            } else {
              const data = new Date(raw);
              if (!isNaN(data.getTime())) {
                const year = data.getUTCFullYear();
                const month = String(data.getUTCMonth() + 1).padStart(2, '0');
                const day = String(data.getUTCDate()).padStart(2, '0');
                setValue('dataNascimento', `${year}-${month}-${day}`);
              }
            }
          }
          if (candidato.pessoaEmail) setValue('email', candidato.pessoaEmail);
          if (candidato.telefone) {
            setValue('telefone', candidato.telefone);
          }
          if (candidato.celular) {
            setValue('celular', candidato.celular);
          }
          if (candidato.endereco) {
            setValue('endereco', candidato.endereco);
          }
          if (candidato.instituicaoEnsinoId != null && candidato.instituicaoEnsinoId !== undefined) {
            setValue('instituicaoEnsinoId', candidato.instituicaoEnsinoId);
          } else if (candidato.instituicaoEnsinoNome) {
            setValue('instituicaoEnsinoId', 'outros' as any);
          }
          if (candidato.instituicaoEnsinoNome) setValue('instituicaoEnsinoNome', candidato.instituicaoEnsinoNome);
          if (candidato.nomeSocial) setValue('nomeSocial', candidato.nomeSocial);
          if (candidato.periodoAtualCurso) setValue('periodoAtualCurso', candidato.periodoAtualCurso);
          if (candidato.anoInicioCurso) setValue('anoInicioCurso', candidato.anoInicioCurso);
          if (candidato.cursoPretendidoId) setValue('cursoPretendidoId', candidato.cursoPretendidoId);
          if (candidato.comarcaPretendidaId) setValue('comarcaPretendidaId', candidato.comarcaPretendidaId);
          if (candidato.distritoPretendidoId) setValue('distritoPretendidoId', candidato.distritoPretendidoId);
          // Armazenar município para setar depois que os municípios forem carregados
          if (candidato.municipio) {
            setMunicipioCandidato(candidato.municipio);
          }
          if (candidato.genero) setValue('genero', candidato.genero as any);
          if (candidato.raca) setValue('raca', candidato.raca as any);
          if (candidato.optaVagasNegros !== undefined) setValue('optaVagasNegros', candidato.optaVagasNegros);
          if (candidato.declaraIndigena !== undefined) setValue('declaraIndigena', candidato.declaraIndigena);

          // Carregar pessoaComDeficiencia diretamente do backend (agora temos essa coluna)
          let pessoaComDeficienciaValue: boolean;

          if (candidato.pessoaComDeficiencia !== undefined) {
            pessoaComDeficienciaValue = candidato.pessoaComDeficiencia;
            setValue('pessoaComDeficiencia', pessoaComDeficienciaValue);
          } else {
            // Fallback: se não houver valor salvo, inferir baseado nos dados existentes
            // (para candidatos antigos que não têm esse campo preenchido)
            const temCamposDeficiencia = !!(candidato.descricaoDeficiencia && candidato.descricaoDeficiencia.trim() !== '') ||
              !!(candidato.laudoMedicoUrl && candidato.laudoMedicoUrl.trim() !== '') ||
              candidato.necessitaCuidadosEspeciais !== undefined;

            if (candidato.optaVagasPcd !== undefined && candidato.optaVagasPcd !== null) {
              pessoaComDeficienciaValue = true;
            } else if (temCamposDeficiencia) {
              pessoaComDeficienciaValue = true;
            } else {
              pessoaComDeficienciaValue = false;
            }
            setValue('pessoaComDeficiencia', pessoaComDeficienciaValue);
          }

          // Só carregar campos relacionados a deficiência se pessoaComDeficiencia for true
          if (pessoaComDeficienciaValue === true) {
            if (candidato.optaVagasPcd !== undefined) setValue('optaVagasPcd', candidato.optaVagasPcd);
            if (candidato.descricaoDeficiencia) setValue('descricaoDeficiencia', candidato.descricaoDeficiencia);
            if (candidato.necessitaCuidadosEspeciais !== undefined) setValue('necessitaCuidadosEspeciais', candidato.necessitaCuidadosEspeciais);
          } else {
            // Limpar campos relacionados quando pessoaComDeficiencia é false
            setValue('optaVagasPcd', undefined);
            setValue('descricaoDeficiencia', undefined);
            setValue('necessitaCuidadosEspeciais', undefined);
            setValue('laudoMedicoUrl', undefined);
          }
          if (candidato.declaracaoVeracidade !== undefined) setValue('declaracaoVeracidade', candidato.declaracaoVeracidade);
          if (candidato.confirmacaoEdital !== undefined) setValue('confirmacaoEdital', candidato.confirmacaoEdital);
          if (candidato.consentimentoLgpd !== undefined) setValue('consentimentoLgpd', candidato.consentimentoLgpd);

          // Carregar arquivos existentes (se houver)
          if (candidato.documentacaoIndigenaUrl) setValue('documentacaoIndigenaUrl', candidato.documentacaoIndigenaUrl);
          if (candidato.laudoMedicoUrl) setValue('laudoMedicoUrl', candidato.laudoMedicoUrl);
          if (candidato.documentoIdentificacaoUrl) setValue('documentoIdentificacaoUrl', candidato.documentoIdentificacaoUrl);
          if (candidato.comprovanteResidenciaUrl) setValue('comprovanteResidenciaUrl', candidato.comprovanteResidenciaUrl);
          if (candidato.documentoEstudoUrl) setValue('documentoEstudoUrl', candidato.documentoEstudoUrl);
          if (candidato.documentosComplementaresUrl) setValue('documentosComplementaresUrl', candidato.documentosComplementaresUrl);
        })
        .catch((error) => {
          console.error('Erro ao carregar dados do candidato:', error);
          toast.warn('Não foi possível carregar os dados da inscrição. Você pode preencher o formulário novamente.');
        });
    }
  }, [candidatoId, processoSeletivoId, setValue]);

  useEffect(() => {
    // Carregar cursos disponíveis quando curso pretendido mudar (endpoint público)
    if (cursoPretendidoId && processoSeletivo) {
      cursoService.findAllPublic({ skip: 0, take: 9999 })
        .then((result) => {
          setCursos(result[0]);
        })
        .catch((error) => {
          console.error('Falha ao carregar cursos:', error);
        });
    }

    // Limpar município selecionado quando o curso mudar (exceto se estiver carregando dados do candidato)
    if (cursoPretendidoId && !candidatoId) {
      setValue('municipio', '');
    }
  }, [cursoPretendidoId, processoSeletivo, setValue, candidatoId]);

  // Setar município quando os municípios estiverem carregados e o curso pretendido estiver definido
  useEffect(() => {
    if (municipioCandidato && municipiosDisponiveis.length > 0 && cursoPretendidoId && processoSeletivo) {
      // Verificar se o município existe nas vagas do curso selecionado
      const municipioExisteNasVagas = processoSeletivo.vagas?.some(v =>
        v.cursoId === cursoPretendidoId && v.municipio === municipioCandidato
      );

      // Verificar se o município existe na lista de municípios disponíveis
      const municipioExisteNaLista = municipiosDisponiveis.some(m => m.nome === municipioCandidato);

      if (municipioExisteNasVagas || municipioExisteNaLista) {
        // Usar setTimeout para garantir que o select já foi renderizado com as opções
        setTimeout(() => {
          setValue('municipio', municipioCandidato, { shouldValidate: true, shouldDirty: false });
        }, 200);
      }
    }
  }, [municipioCandidato, municipiosDisponiveis, cursoPretendidoId, processoSeletivo, setValue]);

  useEffect(() => {
    // Carregar distritos quando comarca mudar (endpoint público)
    if (comarcaPretendidaId) {
      readDistritoPublic({ comarca_id: comarcaPretendidaId, skip: 0, take: 9999, comDefensoria: true } as any)
        .then((result) => {
          setDistritos(result[0]);
        })
        .catch((error) => {
          console.error('Falha ao carregar distritos:', error);
          setDistritos([]);
        });
    } else {
      setDistritos([]);
    }
  }, [comarcaPretendidaId]);

  // Limpar campos de arquivo quando as condições não forem atendidas
  // Removido: não limpar documentacaoIndigenaUrl baseado em declaraIndigena

  useEffect(() => {
    if (!optaVagasPcd) {
      setValue('laudoMedicoUrl', undefined);
      setValue('descricaoDeficiencia', undefined);
      setValue('necessitaCuidadosEspeciais', false);
    }
  }, [optaVagasPcd, setValue]);

  useEffect(() => {
    if (raca === 'branca') {
      setValue('optaVagasNegros', undefined);
      setValue('declaraIndigena', undefined);
      setValue('documentacaoIndigenaUrl', undefined);
    }
    // Limpar documentacaoIndigenaUrl quando a raça não for indígena
    if (raca && raca !== 'indigena') {
      setValue('declaraIndigena', undefined);
      setValue('documentacaoIndigenaUrl', undefined);
    }
  }, [raca, setValue]);

  const buscarCEP = async (cep: string) => {
    const cepLimpo = cep.replace(/\D/g, '');
    if (cepLimpo.length === 8) {
      setIsLoadingCEP(true);
      try {
        const resultado = await BuscaCEP(cepLimpo);

        // Verificar se o resultado é válido e não tem erro
        if (resultado && resultado.erro !== true) {
          // Manter o CEP formatado (com hífen)
          const cepFormatado = cepLimpo.substring(0, 5) + '-' + cepLimpo.substring(5, 8);

          // Preencher os campos do endereço
          setValue('endereco.cep', cepFormatado, { shouldValidate: true, shouldDirty: true });

          // Preencher logradouro (rua/avenida)
          if (resultado.logradouro) {
            setValue('endereco.logradouro', resultado.logradouro, { shouldValidate: true, shouldDirty: true });
          }

          // Preencher bairro
          if (resultado.bairro) {
            setValue('endereco.bairro', resultado.bairro, { shouldValidate: true, shouldDirty: true });
          }

          // Preencher cidade (localidade)
          if (resultado.localidade) {
            setValue('endereco.cidade', resultado.localidade, { shouldValidate: true, shouldDirty: true });
          }

          // Preencher estado (UF)
          if (resultado.uf) {
            setValue('endereco.estado', resultado.uf, { shouldValidate: true, shouldDirty: true });
          }
        } else if (resultado && resultado.erro === true) {
          // CEP não encontrado na base de dados
          toast.warning('CEP não encontrado. Verifique o CEP digitado.');
          // Limpar campos quando CEP não encontrado
          setValue('endereco.logradouro', '', { shouldValidate: false });
          setValue('endereco.bairro', '', { shouldValidate: false });
          setValue('endereco.cidade', '', { shouldValidate: false });
          setValue('endereco.estado', '', { shouldValidate: false });
        } else {
          // Resultado undefined ou null
          toast.warning('CEP não encontrado. Verifique o CEP digitado.');
          // Limpar campos quando resultado é undefined
          setValue('endereco.logradouro', '', { shouldValidate: false });
          setValue('endereco.bairro', '', { shouldValidate: false });
          setValue('endereco.cidade', '', { shouldValidate: false });
          setValue('endereco.estado', '', { shouldValidate: false });
        }
      } catch (error) {
        toast.error('Erro ao buscar CEP. Tente novamente.');
        console.error('Erro ao buscar CEP:', error);
        // Limpar campos em caso de erro
        setValue('endereco.logradouro', '', { shouldValidate: false });
        setValue('endereco.bairro', '', { shouldValidate: false });
        setValue('endereco.cidade', '', { shouldValidate: false });
        setValue('endereco.estado', '', { shouldValidate: false });
      } finally {
        setIsLoadingCEP(false);
      }
    } else {
      setIsLoadingCEP(false);
    }
  };

  const handleDownloadFile = async (fileId: string, filename: string) => {
    try {
      // Usar instância do axios sem interceptors para garantir que o token do candidato seja usado
      const candidatoToken = localStorage.getItem('candidato_token');
      const candidatoApi = axios.create({
        baseURL: API_URL as string,
      });

      // Adicionar token do candidato se disponível, senão usar token padrão
      candidatoApi.interceptors.request.use((config) => {
        const token = candidatoToken || localStorage.getItem('@defensor-token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        if (!(config.data instanceof FormData)) {
          config.headers['Content-Type'] = 'application/json;charset=UTF-8';
        }
        return config;
      });

      const response = await candidatoApi.get(Endpoints.CANDIDATO + '/file/' + fileId, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || 'arquivo.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erro ao baixar arquivo.');
    }
  };

  const handleViewFile = async (fileId: string) => {
    try {
      // Usar instância do axios sem interceptors para garantir que o token do candidato seja usado
      const candidatoToken = localStorage.getItem('candidato_token');
      const candidatoApi = axios.create({
        baseURL: API_URL as string,
      });

      // Adicionar token do candidato se disponível, senão usar token padrão
      candidatoApi.interceptors.request.use((config) => {
        const token = candidatoToken || localStorage.getItem('@defensor-token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        if (!(config.data instanceof FormData)) {
          config.headers['Content-Type'] = 'application/json;charset=UTF-8';
        }
        return config;
      });

      const response = await candidatoApi.get(Endpoints.CANDIDATO + '/file/' + fileId, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (error) {
      toast.error('Erro ao abrir arquivo.');
    }
  };

  // Função para obter nome amigável do campo
  const getFieldName = (fieldPath: string): string => {
    const fieldNames: { [key: string]: string } = {
      'nome': 'Nome Completo',
      'cpf': 'CPF',
      'dataNascimento': 'Data de Nascimento',
      'genero': 'Gênero',
      'email': 'E-mail',
      'emailConfirmacao': 'Confirme o E-mail',
      'celular.ddd': 'DDD do Celular',
      'celular.numero': 'Número do Celular',
      'endereco.cep': 'CEP',
      'endereco.logradouro': 'Logradouro',
      'endereco.numero': 'Número',
      'endereco.complemento': 'Complemento',
      'endereco.bairro': 'Bairro',
      'endereco.cidade': 'Cidade',
      'endereco.estado': 'UF',
      'instituicaoEnsinoId': 'Instituição de Ensino',
      'instituicaoEnsinoNome': 'Nome da Instituição de Ensino',
      'periodoAtualCurso': 'Período Atual do Curso',
      'anoInicioCurso': 'Ano de Início do Curso',
      'cursoPretendidoId': 'Curso Pretendido',
      'municipio': 'Município',
      'raca': 'Identificação Racial',
      'optaVagasNegros': 'Opta por concorrer às vagas reservadas',
      'pessoaComDeficiencia': 'Pessoa com deficiência?',
      'optaVagasPcd': 'Opta por concorrer às vagas reservadas para pessoa com deficiência?',
      'descricaoDeficiencia': 'Descrição da deficiência',
      'documentoIdentificacaoUrl': 'Documento de Identificação (Oficial)',
      'documentoEstudoUrl': 'Histórico Escolar (Graduação)',
      'comprovanteResidenciaUrl': 'Comprovante de Residência',
      'laudoMedicoUrl': 'Laudo Médico (PCD)',
      'documentacaoIndigenaUrl': 'Documentação Indígena',
      'declaracaoVeracidade': 'Declaração de veracidade das informações prestadas',
      'confirmacaoEdital': 'Confirmação de ciência e concordância com o edital do processo seletivo',
      'consentimentoLgpd': 'Consentimento para tratamento de dados pessoais (LGPD)',
    };

    return fieldNames[fieldPath] || fieldPath;
  };

  // Mapeamento de campos para mensagens de erro amigáveis (usado em processAndShowErrors)
  const getFieldErrorMessage = (fieldPath: string): string => {
    const fieldMessages: { [key: string]: string } = {
      // Dados Pessoais
      'nome': 'O campo "Nome Completo" é obrigatório. Por favor, preencha este campo.',
      'cpf': 'O campo "CPF" é obrigatório. Por favor, preencha este campo.',
      'dataNascimento': 'O campo "Data de Nascimento" é obrigatório. Por favor, preencha este campo.',
      'genero': 'O campo "Gênero" é obrigatório. Por favor, selecione uma opção.',
      // Contatos
      'email': 'O campo "E-mail" é obrigatório. Por favor, preencha este campo.',
      'emailConfirmacao': 'O campo "Confirme o E-mail" é obrigatório. Por favor, preencha este campo ou verifique se os e-mails coincidem.',
      'celular.ddd': 'O campo "DDD do Celular" é obrigatório. Por favor, preencha este campo.',
      'celular.numero': 'O campo "Número do Celular" é obrigatório. Por favor, preencha este campo.',
      // Endereço
      'endereco.cep': 'O campo "CEP" é obrigatório. Por favor, preencha este campo.',
      'endereco.logradouro': 'O campo "Logradouro" é obrigatório. Por favor, preencha este campo.',
      'endereco.numero': 'O campo "Número" é obrigatório. Por favor, preencha este campo.',
      'endereco.complemento': 'O campo "Complemento" é obrigatório. Por favor, preencha este campo.',
      'endereco.bairro': 'O campo "Bairro" é obrigatório. Por favor, preencha este campo.',
      'endereco.cidade': 'O campo "Cidade" é obrigatório. Por favor, preencha este campo.',
      'endereco.estado': 'O campo "UF" é obrigatório. Por favor, preencha este campo.',
      // Dados Acadêmicos
      'instituicaoEnsinoId': 'O campo "Instituição de Ensino" é obrigatório. Por favor, selecione uma opção ou escolha "Outros".',
      'instituicaoEnsinoNome': 'O campo "Nome da Instituição de Ensino" é obrigatório. Por favor, preencha este campo.',
      'periodoAtualCurso': 'O campo "Período Atual do Curso" é obrigatório. Por favor, preencha este campo.',
      'anoInicioCurso': 'O campo "Ano de Início do Curso" é obrigatório. Por favor, preencha este campo.',
      // Curso e Município
      'cursoPretendidoId': 'O campo "Curso Pretendido" é obrigatório. Por favor, selecione uma opção.',
      'municipio': 'O campo "Município" é obrigatório. Por favor, selecione uma opção.',
      // Identificação Étnico-Racial
      'raca': 'O campo "Identificação Racial" é obrigatório. Por favor, selecione uma opção.',
      'optaVagasNegros': 'O campo "Opta por concorrer às vagas reservadas" é obrigatório. Por favor, selecione uma opção.',
      // Pessoa com Deficiência
      'pessoaComDeficiencia': 'O campo "Pessoa com deficiência?" é obrigatório. Por favor, selecione uma opção.',
      'optaVagasPcd': 'O campo "Opta por concorrer às vagas reservadas para pessoa com deficiência?" é obrigatório. Por favor, selecione uma opção.',
      'descricaoDeficiencia': 'O campo "Descrição da deficiência" é obrigatório. Por favor, preencha este campo.',
      // Documentos
      'documentoIdentificacaoUrl': 'O campo "Documento de Identificação (Oficial)" é obrigatório. Por favor, anexe o documento.',
      'documentoEstudoUrl': 'O campo "Histórico Escolar (Graduação)" é obrigatório. Por favor, anexe o documento.',
      'comprovanteResidenciaUrl': 'O campo "Comprovante de Residência" é obrigatório. Por favor, anexe o documento.',
      'laudoMedicoUrl': 'O campo "Laudo Médico (PCD)" é obrigatório. Por favor, anexe o documento.',
      'documentacaoIndigenaUrl': 'O campo "Documentação Indígena" é obrigatório. Por favor, anexe o documento.',
      // Declarações
      'declaracaoVeracidade': 'A "Declaração de veracidade das informações prestadas" é obrigatória. Por favor, marque esta opção.',
      'confirmacaoEdital': 'A "Confirmação de ciência e concordância com o edital do processo seletivo" é obrigatória. Por favor, marque esta opção.',
      'consentimentoLgpd': 'O "Consentimento para tratamento de dados pessoais (LGPD)" é obrigatório. Por favor, marque esta opção.',
    };

    return fieldMessages[fieldPath] || `O campo "${fieldPath}" é obrigatório. Por favor, preencha este campo.`;
  };

  // Função para validar campos obrigatórios manualmente
  // Retorna uma lista de todos os campos com erro
  const validateRequiredFields = (data: CreateCandidatoDto): Array<{ field: string; isEmailMismatch?: boolean }> => {
    const errors: Array<{ field: string; isEmailMismatch?: boolean }> = [];

    // Dados Pessoais
    if (!data.nome || data.nome.trim() === '') {
      errors.push({ field: 'nome' });
    }
    if (!data.cpf || data.cpf.trim() === '') {
      errors.push({ field: 'cpf' });
    }
    if (!data.dataNascimento) {
      errors.push({ field: 'dataNascimento' });
    }
    if (!data.genero) {
      errors.push({ field: 'genero' });
    }

    // Contatos
    if (!data.email || data.email.trim() === '') {
      errors.push({ field: 'email' });
    }
    if (!data.emailConfirmacao || data.emailConfirmacao.trim() === '') {
      errors.push({ field: 'emailConfirmacao' });
    }
    if (data.email && data.emailConfirmacao && data.email !== data.emailConfirmacao) {
      errors.push({ field: 'emailConfirmacao', isEmailMismatch: true });
    }
    if (!data.celular?.ddd || (typeof data.celular.ddd === 'string' && data.celular.ddd.trim() === '')) {
      errors.push({ field: 'celular.ddd' });
    }
    if (!data.celular?.numero || data.celular.numero.trim() === '') {
      errors.push({ field: 'celular.numero' });
    }

    // Endereço
    if (!data.endereco?.cep || data.endereco.cep.trim() === '') {
      errors.push({ field: 'endereco.cep' });
    }
    if (!data.endereco?.logradouro || data.endereco.logradouro.trim() === '') {
      errors.push({ field: 'endereco.logradouro' });
    }
    if (!data.endereco?.numero) {
      errors.push({ field: 'endereco.numero' });
    }
    if (!data.endereco?.complemento || data.endereco.complemento.trim() === '') {
      errors.push({ field: 'endereco.complemento' });
    }
    if (!data.endereco?.bairro || data.endereco.bairro.trim() === '') {
      errors.push({ field: 'endereco.bairro' });
    }
    if (!data.endereco?.cidade || data.endereco.cidade.trim() === '') {
      errors.push({ field: 'endereco.cidade' });
    }
    if (!data.endereco?.estado || data.endereco.estado.trim() === '') {
      errors.push({ field: 'endereco.estado' });
    }

    // Dados Acadêmicos: instituição válida se tem ID/outros OU se preencheu o nome (Outros)
    const instituicaoIdStr = String(data.instituicaoEnsinoId ?? '');
    const temOutrosComNome = data.instituicaoEnsinoNome != null && String(data.instituicaoEnsinoNome).trim() !== '';
    const instituicaoPreenchida = (instituicaoIdStr !== '' && instituicaoIdStr !== 'undefined') || temOutrosComNome;
    if (!instituicaoPreenchida) {
      errors.push({ field: 'instituicaoEnsinoId' });
    }
    if ((instituicaoIdStr === 'outros' || temOutrosComNome) && (!data.instituicaoEnsinoNome || data.instituicaoEnsinoNome.trim() === '')) {
      errors.push({ field: 'instituicaoEnsinoNome' });
    }
    if (!data.periodoAtualCurso) {
      errors.push({ field: 'periodoAtualCurso' });
    }
    if (!data.anoInicioCurso) {
      errors.push({ field: 'anoInicioCurso' });
    }

    // Curso e Município
    if (!data.cursoPretendidoId) {
      errors.push({ field: 'cursoPretendidoId' });
    }
    if (!data.municipio || data.municipio.trim() === '') {
      errors.push({ field: 'municipio' });
    }

    // Identificação Étnico-Racial
    if (!data.raca) {
      errors.push({ field: 'raca' });
    }
    // Se raça não é branca nem amarela, optaVagasNegros é obrigatório
    if (data.raca !== 'branca' && data.raca !== 'amarela' && data.optaVagasNegros === undefined) {
      errors.push({ field: 'optaVagasNegros' });
    }

    // Pessoa com Deficiência
    if (data.pessoaComDeficiencia === undefined) {
      errors.push({ field: 'pessoaComDeficiencia' });
    }
    if (data.pessoaComDeficiencia === true) {
      if (data.optaVagasPcd === undefined) {
        errors.push({ field: 'optaVagasPcd' });
      }
      if (data.optaVagasPcd === true) {
        if (!data.descricaoDeficiencia || data.descricaoDeficiencia.trim() === '') {
          errors.push({ field: 'descricaoDeficiencia' });
        }
      }
    }

    // Documentos obrigatórios
    // Verificar se documentoIdentificacaoUrl existe (pode ser File ou string)
    const hasDocumentoIdentificacao = data.documentoIdentificacaoUrl &&
      ((data.documentoIdentificacaoUrl as unknown) instanceof File ||
        (typeof data.documentoIdentificacaoUrl === 'string' && data.documentoIdentificacaoUrl.trim() !== ''));
    if (!hasDocumentoIdentificacao) {
      errors.push({ field: 'documentoIdentificacaoUrl' });
    }

    // Verificar se documentoEstudoUrl existe
    const hasDocumentoEstudo = data.documentoEstudoUrl &&
      ((data.documentoEstudoUrl as unknown) instanceof File ||
        (typeof data.documentoEstudoUrl === 'string' && data.documentoEstudoUrl.trim() !== ''));
    if (!hasDocumentoEstudo) {
      errors.push({ field: 'documentoEstudoUrl' });
    }

    // Verificar se comprovanteResidenciaUrl existe
    const hasComprovanteResidencia = data.comprovanteResidenciaUrl &&
      ((data.comprovanteResidenciaUrl as unknown) instanceof File ||
        (typeof data.comprovanteResidenciaUrl === 'string' && data.comprovanteResidenciaUrl.trim() !== ''));
    if (!hasComprovanteResidencia) {
      errors.push({ field: 'comprovanteResidenciaUrl' });
    }

    // Laudo médico é obrigatório se optaVagasPcd é true
    if (data.optaVagasPcd === true) {
      const hasLaudoMedico = data.laudoMedicoUrl &&
        ((data.laudoMedicoUrl as unknown) instanceof File ||
          (typeof data.laudoMedicoUrl === 'string' && data.laudoMedicoUrl.trim() !== ''));
      if (!hasLaudoMedico) {
        errors.push({ field: 'laudoMedicoUrl' });
      }
    }

    // Documentação indígena é obrigatória se raça é indígena e opta por vagas
    if (data.raca === 'indigena' && data.optaVagasNegros === true) {
      const hasDocumentacaoIndigena = data.documentacaoIndigenaUrl &&
        ((data.documentacaoIndigenaUrl as unknown) instanceof File ||
          (typeof data.documentacaoIndigenaUrl === 'string' && data.documentacaoIndigenaUrl.trim() !== ''));
      if (!hasDocumentacaoIndigena) {
        errors.push({ field: 'documentacaoIndigenaUrl' });
      }
    }

    // Declarações
    if (!data.declaracaoVeracidade) {
      errors.push({ field: 'declaracaoVeracidade' });
    }
    if (!data.confirmacaoEdital) {
      errors.push({ field: 'confirmacaoEdital' });
    }
    if (!data.consentimentoLgpd) {
      errors.push({ field: 'consentimentoLgpd' });
    }

    return errors;
  };

  // Função auxiliar para encontrar todos os erros no objeto de erros
  const findAllErrors = (obj: any, path = ''): string[] => {
    const errorsList: string[] = [];
    for (const key in obj) {
      const currentPath = path ? `${path}.${key}` : key;
      if (obj[key]?.message) {
        errorsList.push(currentPath);
      }
      if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
        errorsList.push(...findAllErrors(obj[key], currentPath));
      }
    }
    return errorsList;
  };

  // Função para processar e exibir erros
  const processAndShowErrors = (formErrors: any, formData?: CreateCandidatoDto) => {
    // Validar campos obrigatórios manualmente
    const validationErrors = formData ? validateRequiredFields(formData) : [];

    // Combinar erros da validação manual e do react-hook-form
    const allErrors: string[] = [];

    // Adicionar erros da validação manual primeiro (mais confiável)
    const emailMismatchError = validationErrors.find(e => e.isEmailMismatch);
    const otherManualErrors = validationErrors.filter(e => !e.isEmailMismatch);

    otherManualErrors.forEach(err => {
      if (!allErrors.includes(err.field)) {
        allErrors.push(err.field);
      }
    });

    // Se escolheu "Outros" e preencheu o nome, não exibir erro de "Instituição de Ensino"
    const temOutrosComNome = formData?.instituicaoEnsinoNome != null && String(formData.instituicaoEnsinoNome).trim() !== '';

    // Adicionar erros do react-hook-form (exceto instituicaoEnsinoId quando Outros + nome preenchido)
    if (formErrors) {
      const reactHookFormErrors = findAllErrors(formErrors);
      reactHookFormErrors.forEach(err => {
        if (err === 'instituicaoEnsinoId' && temOutrosComNome) return;
        if (!allErrors.includes(err)) {
          allErrors.push(err);
        }
      });
    }
    if (temOutrosComNome && allErrors.includes('instituicaoEnsinoId')) {
      const idx = allErrors.indexOf('instituicaoEnsinoId');
      allErrors.splice(idx, 1);
    }

    // Garantir que sempre temos erros para mostrar
    if (allErrors.length === 0 && validationErrors.length > 0) {
      validationErrors.forEach(err => {
        if (!allErrors.includes(err.field)) {
          allErrors.push(err.field);
        }
      });
    }

    // Construir mensagem com todos os erros
    let errorMessage = 'Por favor, preencha os seguintes campos obrigatórios:\n\n';

    // Adicionar erros de campos vazios
    if (allErrors.length > 0) {
      const errorMessages = allErrors.map(field => {
        const msg = getFieldErrorMessage(field);
        return `• ${msg}`;
      });
      errorMessage += errorMessages.join('\n');
    } else if (validationErrors.length > 0) {
      // Se não encontramos erros específicos mas temos validationErrors, usar eles
      const errorMessages = validationErrors.map(err => {
        const msg = getFieldErrorMessage(err.field);
        return `• ${msg}`;
      });
      errorMessage += errorMessages.join('\n');
    } else {
      // Se não conseguimos identificar campos específicos, usar mensagem genérica
      errorMessage += '• Existem campos obrigatórios que precisam ser preenchidos';
    }

    // Adicionar erro de email mismatch se houver
    if (emailMismatchError) {
      if (allErrors.length > 0 || validationErrors.length > 0) {
        errorMessage += '\n\n';
      }
      errorMessage += '• Os e-mails não coincidem';
    }

    // SEMPRE mostrar toast - sem condições
    // Garantir que sempre mostramos algo
    if (allErrors.length === 0 && validationErrors.length === 0 && !emailMismatchError) {
      errorMessage = 'Por favor, preencha todos os campos obrigatórios marcados com asterisco (*).';
    }

    // Chamar toast de forma síncrona - SEMPRE
    // Forçar exibição do toast
    toast.error(errorMessage, {
      autoClose: 10000,
      style: { whiteSpace: 'pre-line' },
      position: 'top-right',
    });

    // Garantir que o toast apareça - tentar novamente após um pequeno delay se necessário
    setTimeout(() => {
      // Verificar se o toast foi exibido (não há como verificar diretamente, mas podemos tentar novamente)
    }, 100);
  };

  // Callback para quando há erros de validação do react-hook-form
  const onError = (formErrors: any) => {
    // Obter os dados atuais do formulário
    const currentData = watch();

    // SEMPRE mostrar toast quando onError é chamado
    // Aguardar um pouco para garantir que os erros foram atualizados
    setTimeout(() => {
      processAndShowErrors(formErrors, currentData);
    }, 100);
  };

  const onSubmit: SubmitHandler<CreateCandidatoDto> = async (data) => {

    // Validar campos obrigatórios manualmente primeiro
    const validationErrors = validateRequiredFields(data);

    // Se houver erros na validação manual, mostrar toast e retornar
    if (validationErrors.length > 0) {
      processAndShowErrors({}, data); // Passar objeto vazio para formErrors, pois já temos validationErrors
      return;
    }

    // Validar captcha antes de enviar
    if (!captchaToken) {
      toast.error('Por favor, complete a verificação do captcha antes de enviar o formulário.');
      return;
    }

    setIsLoading(true);

    try {
      // Remover formatação do CPF (deixar apenas números)
      const cpfLimpo = data.cpf ? data.cpf.replace(/\D/g, '') : '';

      // Remover formatação do CEP (deixar apenas números)
      const cepLimpo = data.endereco?.cep ? data.endereco.cep.replace(/\D/g, '') : undefined;

      // Se pessoaComDeficiencia é false, optaVagasPcd deve ser undefined (não enviar)
      // Se pessoaComDeficiencia é true, usar o valor de optaVagasPcd (true ou false)
      const optaVagasPcdValue = data.pessoaComDeficiencia === false
        ? undefined
        : (typeof data.optaVagasPcd === 'string'
          ? data.optaVagasPcd === 'true'
          : data.optaVagasPcd);

      // Fazer upload dos arquivos obrigatórios
      let documentoIdentificacaoFileId: string | undefined;
      let comprovanteResidenciaFileId: string | undefined;
      let documentoEstudoFileId: string | undefined;
      let documentosComplementaresFileId: string | undefined;
      let documentacaoIndigenaFileId: string | undefined;
      let laudoMedicoFileId: string | undefined;

      // Upload do documento de identificação (obrigatório na criação; opcional na edição)
      const docIdentificacaoFile = data.documentoIdentificacaoUrl && typeof data.documentoIdentificacaoUrl !== 'string'
        ? data.documentoIdentificacaoUrl as File
        : null;

      if (docIdentificacaoFile) {
        try {
          documentoIdentificacaoFileId = await candidatoService.uploadFile(
            docIdentificacaoFile,
            'documento_identificacao'
          );
        } catch (error) {
          console.error('Erro ao fazer upload do documento de identificação:', error);
          toast.error('Erro ao fazer upload do documento de identificação. Tente novamente.');
          setIsLoading(false);
          return;
        }
      } else if (typeof data.documentoIdentificacaoUrl === 'string' && data.documentoIdentificacaoUrl.trim() !== '') {
        // Edição: manter o arquivo já existente
        documentoIdentificacaoFileId = data.documentoIdentificacaoUrl as any;
      } else {
        // Criação sem arquivo
        toast.error('Por favor, anexe o documento de identificação (RG).');
        setIsLoading(false);
        return;
      }

      // Upload do comprovante de residência (obrigatório na criação; opcional na edição)
      const comprovanteFile = data.comprovanteResidenciaUrl && typeof data.comprovanteResidenciaUrl !== 'string'
        ? data.comprovanteResidenciaUrl as File
        : null;

      if (comprovanteFile) {
        try {
          comprovanteResidenciaFileId = await candidatoService.uploadFile(
            comprovanteFile,
            'comprovante_residencia'
          );
        } catch (error) {
          console.error('Erro ao fazer upload do comprovante de residência:', error);
          toast.error('Erro ao fazer upload do comprovante de residência. Tente novamente.');
          setIsLoading(false);
          return;
        }
      } else if (typeof data.comprovanteResidenciaUrl === 'string' && data.comprovanteResidenciaUrl.trim() !== '') {
        comprovanteResidenciaFileId = data.comprovanteResidenciaUrl as any;
      } else {
        toast.error('Por favor, anexe o comprovante de residência.');
        setIsLoading(false);
        return;
      }

      // Upload do documento de estudo (obrigatório na criação; opcional na edição)
      const docEstudoFile = data.documentoEstudoUrl && typeof data.documentoEstudoUrl !== 'string'
        ? data.documentoEstudoUrl as File
        : null;

      if (docEstudoFile) {
        try {
          documentoEstudoFileId = await candidatoService.uploadFile(
            docEstudoFile,
            'documento_estudo'
          );
        } catch (error) {
          console.error('Erro ao fazer upload do documento de estudo:', error);
          toast.error('Erro ao fazer upload do documento de estudo. Tente novamente.');
          setIsLoading(false);
          return;
        }
      } else if (typeof data.documentoEstudoUrl === 'string' && data.documentoEstudoUrl.trim() !== '') {
        documentoEstudoFileId = data.documentoEstudoUrl as any;
      } else {
        toast.error('Por favor, anexe o documento de estudo da faculdade.');
        setIsLoading(false);
        return;
      }

      // Upload dos documentos complementares (opcional)
      const docComplementaresFile = data.documentosComplementaresUrl && typeof data.documentosComplementaresUrl !== 'string'
        ? data.documentosComplementaresUrl as File
        : null;

      if (docComplementaresFile) {
        try {
          documentosComplementaresFileId = await candidatoService.uploadFile(
            docComplementaresFile,
            'documentos_complementares'
          );
        } catch (error) {
          console.error('Erro ao fazer upload dos documentos complementares:', error);
          toast.error('Erro ao fazer upload dos documentos complementares. Tente novamente.');
          setIsLoading(false);
          return;
        }
      } else if (typeof data.documentosComplementaresUrl === 'string' && data.documentosComplementaresUrl.trim() !== '') {
        documentosComplementaresFileId = data.documentosComplementaresUrl as any;
      }

      // Verificar se há arquivo para documentação indígena (opcional)
      // Aparece quando raça é indígena e opta por concorrer às vagas reservadas
      const docIndigenaFile = data.documentacaoIndigenaUrl && typeof data.documentacaoIndigenaUrl !== 'string'
        ? data.documentacaoIndigenaUrl as File
        : null;

      const optaVagasIndigenas = data.raca === 'indigena' && data.optaVagasNegros === true;

      if (optaVagasIndigenas && docIndigenaFile) {
        try {
          documentacaoIndigenaFileId = await candidatoService.uploadFile(
            docIndigenaFile,
            'documentacao_indigena'
          );
        } catch (error) {
          console.error('Erro ao fazer upload da documentação indígena:', error);
          toast.error('Erro ao fazer upload da documentação indígena. Tente novamente.');
          setIsLoading(false);
          return;
        }
      }

      // Verificar se há arquivo para laudo médico (opcional)
      const laudoFile = data.laudoMedicoUrl && typeof data.laudoMedicoUrl !== 'string'
        ? data.laudoMedicoUrl as File
        : null;

      if (optaVagasPcdValue && laudoFile) {
        try {
          laudoMedicoFileId = await candidatoService.uploadFile(
            laudoFile,
            'laudo_medico'
          );
        } catch (error) {
          console.error('Erro ao fazer upload do laudo médico:', error);
          toast.error('Erro ao fazer upload do laudo médico. Tente novamente.');
          setIsLoading(false);
          return;
        }
      }

      // Limpar formatação dos telefones e manter DDD como string
      const telefoneLimpo = data.telefone ? {
        ddd: data.telefone.ddd ? data.telefone.ddd.replace(/\D/g, '') : undefined,
        numero: data.telefone.numero ? data.telefone.numero.replace(/\D/g, '') : undefined,
      } : undefined;

      const celularLimpo = data.celular ? {
        ddd: data.celular.ddd ? data.celular.ddd.replace(/\D/g, '') : undefined,
        numero: data.celular.numero ? data.celular.numero.replace(/\D/g, '') : undefined,
      } : undefined;

      // Validar instituição de ensino: aceita ID da lista ou "Outros" com nome preenchido
      const instituicaoEnsinoIdStr = String(data.instituicaoEnsinoId ?? '');
      const temOutrosComNome = data.instituicaoEnsinoNome != null && String(data.instituicaoEnsinoNome).trim() !== '';

      let instituicaoEnsinoIdFinal: number | undefined;
      let instituicaoEnsinoNomeFinal: string | undefined;

      if (instituicaoEnsinoIdStr === 'outros' || (instituicaoEnsinoIdStr === '' && temOutrosComNome)) {
        if (!data.instituicaoEnsinoNome || data.instituicaoEnsinoNome.trim() === '') {
          toast.error('Por favor, informe o nome da instituição de ensino quando escolher "Outros".');
          setIsLoading(false);
          return;
        }
        instituicaoEnsinoIdFinal = undefined;
        instituicaoEnsinoNomeFinal = data.instituicaoEnsinoNome.trim();
      } else if (instituicaoEnsinoIdStr !== '' && instituicaoEnsinoIdStr !== 'undefined') {
        const idNumero = typeof data.instituicaoEnsinoId === 'number'
          ? data.instituicaoEnsinoId
          : (typeof data.instituicaoEnsinoId === 'string' && data.instituicaoEnsinoId !== '' && data.instituicaoEnsinoId !== 'outros'
            ? Number(data.instituicaoEnsinoId)
            : undefined);
        instituicaoEnsinoIdFinal = idNumero != null && !isNaN(idNumero) ? idNumero : undefined;
        instituicaoEnsinoNomeFinal = undefined;
        if (instituicaoEnsinoIdFinal == null) {
          toast.error('Por favor, selecione uma instituição de ensino ou escolha "Outros".');
          setIsLoading(false);
          return;
        }
      } else {
        toast.error('Por favor, selecione uma instituição de ensino ou escolha "Outros".');
        setIsLoading(false);
        return;
      }

      // Remover apenas emailConfirmacao (campo apenas para validação frontend)
      const { emailConfirmacao, ...dataSemConfirmacao } = data;

      const dadosEnvio: CreateCandidatoDto = {
        ...dataSemConfirmacao,
        captchaToken: captchaToken || undefined,
        cpf: cpfLimpo, // CPF sem formatação
        telefone: telefoneLimpo,
        celular: celularLimpo,
        instituicaoEnsinoId: instituicaoEnsinoIdFinal,
        instituicaoEnsinoNome: instituicaoEnsinoNomeFinal,
        endereco: data.endereco ? {
          ...data.endereco,
          cep: cepLimpo, // CEP sem formatação
        } : undefined,
        municipio: data.municipio && data.municipio.trim() !== '' ? data.municipio.trim() : undefined, // Garantir que o município seja enviado
        optaVagasNegros: typeof data.optaVagasNegros === 'string'
          ? data.optaVagasNegros === 'true'
          : data.optaVagasNegros || false,
        declaraIndigena: data.raca === 'indigena' && data.optaVagasNegros === true, // Auto-definir baseado na raça e opção
        pessoaComDeficiencia: data.pessoaComDeficiencia,
        optaVagasPcd: optaVagasPcdValue,
        // Se pessoaComDeficiencia é false, limpar campos relacionados
        descricaoDeficiencia: data.pessoaComDeficiencia === false ? undefined : data.descricaoDeficiencia,
        necessitaCuidadosEspeciais: data.pessoaComDeficiencia === false
          ? undefined
          : (typeof data.necessitaCuidadosEspeciais === 'string'
            ? data.necessitaCuidadosEspeciais === 'true'
            : data.necessitaCuidadosEspeciais),
        declaracaoVeracidade: data.declaracaoVeracidade || false,
        confirmacaoEdital: data.confirmacaoEdital || false,
        consentimentoLgpd: data.consentimentoLgpd || false,
        documentoIdentificacaoUrl: documentoIdentificacaoFileId,
        comprovanteResidenciaUrl: comprovanteResidenciaFileId,
        documentoEstudoUrl: documentoEstudoFileId,
        documentosComplementaresUrl: documentosComplementaresFileId,
        documentacaoIndigenaUrl: documentacaoIndigenaFileId,
        // Se pessoaComDeficiencia é false, não enviar laudoMedicoUrl (limpar no backend)
        laudoMedicoUrl: data.pessoaComDeficiencia === false ? undefined : laudoMedicoFileId,
        // Não enviar comarcaPretendidaId se não houver valor válido
        comarcaPretendidaId: data.comarcaPretendidaId && data.comarcaPretendidaId > 0 ? data.comarcaPretendidaId : undefined,
      };

      // Remover campos de arquivo opcionais se não forem necessários
      const optaVagasIndigenasFinal = dadosEnvio.raca === 'indigena' && dadosEnvio.optaVagasNegros === true;
      if (!optaVagasIndigenasFinal || !documentacaoIndigenaFileId) {
        delete dadosEnvio.documentacaoIndigenaUrl;
      }

      if (!optaVagasPcdValue || !laudoMedicoFileId) {
        delete dadosEnvio.laudoMedicoUrl;
      }

      // Remover comarcaPretendidaId se não houver valor válido (agora usamos município)
      if (!dadosEnvio.comarcaPretendidaId || dadosEnvio.comarcaPretendidaId <= 0) {
        delete dadosEnvio.comarcaPretendidaId;
      }

      // Se houver candidatoId, atualizar; caso contrário, criar novo
      if (candidatoId) {
        try {
          await candidatoService.updatePublic(candidatoId, dadosEnvio);
          toast.success('Inscrição atualizada com sucesso!');
          // Resetar captcha após envio bem-sucedido
          if (recaptchaRef.current) {
            recaptchaRef.current.reset();
          }
          setCaptchaToken(null);
          // Redirecionar de volta para a lista de processos após atualização
          navigate('/candidato/meus-processos');
        } catch (updateError: any) {
          // Processar erros de validação do backend na atualização
          if (updateError.response?.data?.errors && Array.isArray(updateError.response.data.errors) && updateError.response.data.errors.length > 0) {
            let errorMessage = 'Por favor, preencha os seguintes campos obrigatórios:\n\n';

            const errorMessages = updateError.response.data.errors.map((err: any) => {
              const fieldName = getFieldName(err.field);
              return `• ${fieldName}`;
            });

            errorMessage += errorMessages.join('\n');

            toast.error(errorMessage, {
              autoClose: 10000,
              style: { whiteSpace: 'pre-line' },
            });
          } else {
            toast.error(updateError.response?.data?.message || String(updateError));
          }
          setIsLoading(false);
          return;
        }
      } else {
        await candidatoService.createPublic(dadosEnvio);
        // Resetar captcha após envio bem-sucedido
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
        setCaptchaToken(null);
        // Redirecionar para tela de confirmação com o email, CPF e nome do candidato
        const emailCandidato = data.email || '';
        const cpfCandidato = data.cpf ? data.cpf.replace(/\D/g, '') : ''; // CPF sem formatação
        const nomeCandidato = `${data.nome || ''} ${data.sobrenome || ''}`.trim();

        const params = new URLSearchParams();
        if (emailCandidato) params.append('email', emailCandidato);
        if (cpfCandidato) params.append('cpf', cpfCandidato);
        if (nomeCandidato) params.append('nome', nomeCandidato);

        navigate(`/candidato/inscricao-confirmacao${params.toString() ? `?${params.toString()}` : ''}`);
      }
    } catch (error: any) {
      // Processar erros de validação do backend
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors) && error.response.data.errors.length > 0) {

        // Construir mensagem com todos os erros retornados pelo backend
        let errorMessage = 'Por favor, preencha os seguintes campos obrigatórios:\n\n';

        const errorMessages = error.response.data.errors.map((err: any) => {
          const fieldName = getFieldName(err.field);
          return `• ${fieldName}`;
        });

        errorMessage += errorMessages.join('\n');

        // Mostrar toast com todos os erros
        toast.error(errorMessage, {
          autoClose: 10000,
          style: { whiteSpace: 'pre-line' },
        });
      } else if (error.message) {
        // Se for um erro do ValidateAxiosError (que converte em Error com message)
        toast.error(error.message);
      } else {
        // Erro genérico
        toast.error(error.response?.data?.message || String(error));
      }

      // Resetar captcha em caso de erro
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
      setCaptchaToken(null);
    } finally {
      setIsLoading(false);
    }
  };

  if (!processoSeletivo) {
    return (
      <div className="row">
        <div className="col-12 text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
        </div>
      </div>
    );
  }

  // Filtrar municípios disponíveis baseado nas vagas do processo seletivo
  // Se um curso for selecionado, mostrar apenas os municípios daquele curso
  // Caso contrário, mostrar todos os municípios do processo seletivo
  const municipiosFiltrados = cursoPretendidoId && processoSeletivo.vagas
    ? municipiosDisponiveis.filter(m => {
      // Verificar se o município está nas vagas do curso selecionado
      return processoSeletivo.vagas?.some(v =>
        v.cursoId === cursoPretendidoId && v.municipio === m.nome
      );
    })
    : municipiosDisponiveis;

  // Filtrar comarcas e distritos disponíveis baseado nas vagas do processo seletivo
  const comarcasDisponiveis: number[] = processoSeletivo.vagas
    ?.filter(v => v.cursoId === cursoPretendidoId)
    .map(v => v.comarcaId)
    .filter((v): v is number => v != null)
    .filter((v, i, arr) => arr.indexOf(v) === i) ?? [];

  const distritosDisponiveis = processoSeletivo.vagas
    ?.filter(v => v.cursoId === cursoPretendidoId && v.comarcaId === comarcaPretendidaId)
    .map(v => v.distritoId)
    .filter((v): v is number => v !== undefined && v !== null)
    .filter((v, i, arr) => arr.indexOf(v) === i) || [];

  return (
    <div className="row">
      <div className="card-body text-center p-4 mb-2">
        <img style={{ width: '150px', height: '150px' }} src={logoDefensoria} alt="Logo Defensoria" className="img-fluid" />
      </div>
      <div className="col-md-12 text-center mb-2">
        <h3 className="pt-4"> {processoSeletivo.nome}</h3>
        <p className="text-muted">
          <div className="col-12 text-center mb-4">
            <h3>
              Defensoria Pública do Estado de Sergipe
            </h3>
          </div>
          Período de inscrições: {new Date(processoSeletivo.dataInicioInscricoes).toLocaleDateString('pt-BR')} até {new Date(processoSeletivo.dataFimInscricoes).toLocaleDateString('pt-BR')}
        </p>
        <hr className="pb-4" />
      </div>
      <div>
        <h3 className="text-center">Formulário para Processo Seletivo</h3>
      </div>

      <form onSubmit={async (e) => {
        e.preventDefault();
        e.stopPropagation();

        // Obter dados atuais do formulário
        const currentData = watch();

        // Validar campos obrigatórios manualmente
        const validationErrors = validateRequiredFields(currentData);

        // Se houver erros, mostrar toast e retornar
        if (validationErrors.length > 0) {

          // Construir mensagem de erro
          let errorMessage = 'Por favor, preencha os seguintes campos obrigatórios:\n\n';

          const emailMismatchError = validationErrors.find(e => e.isEmailMismatch);
          const otherErrors = validationErrors.filter(e => !e.isEmailMismatch);

          if (otherErrors.length > 0) {
            const errorMessages = otherErrors.map(err => {
              const fieldName = getFieldName(err.field);
              return `• ${fieldName}`;
            });
            errorMessage += errorMessages.join('\n');
          }

          if (emailMismatchError) {
            if (otherErrors.length > 0) {
              errorMessage += '\n\n';
            }
            errorMessage += '• Os e-mails não coincidem';
          }

          try {
            toast.error(errorMessage, {
              autoClose: 10000,
              style: { whiteSpace: 'pre-line' },
            });
          } catch (toastError) {
            console.error('ERRO ao chamar toast:', toastError);
            // Fallback: usar alert
            alert(errorMessage);
          }

          // Também acionar validação do react-hook-form para mostrar erros visuais
          await trigger(undefined, { shouldFocus: false });

          return;
        }

        // Se não encontrou erros na validação manual, fazer um teste simples
        // Teste: verificar se pelo menos um campo obrigatório está vazio
        if (!currentData.nome || currentData.nome.trim() === '') {
          toast.error('Por favor, preencha o campo "Nome Completo" que é obrigatório.');
          await trigger(undefined, { shouldFocus: false });
          return;
        }

        // Se não houver erros na validação manual, validar com react-hook-form
        const isValid = await trigger(undefined, { shouldFocus: false });

        if (!isValid) {
          const currentData2 = watch();
          const validationErrors2 = validateRequiredFields(currentData2);

          if (validationErrors2.length > 0) {
            let errorMessage = 'Por favor, preencha os seguintes campos obrigatórios:\n\n';
            const errorMessages = validationErrors2.map(err => {
              const fieldName = getFieldName(err.field);
              return `• ${fieldName}`;
            });
            errorMessage += errorMessages.join('\n');

            toast.error(errorMessage, {
              autoClose: 10000,
              style: { whiteSpace: 'pre-line' },
            });
            return;
          }
        }

        // Se passou todas as validações, chamar onSubmit
        handleSubmit(onSubmit as (data: CreateCandidatoDto) => void, onError)(e);
      }}>
        <FormularioInscricao
          register={register}
          setValue={setValue}
          watch={watch}
          errors={errors}
          isLoadingCEP={isLoadingCEP}
          setIsLoadingCEP={setIsLoadingCEP}
          buscarCEP={buscarCEP}
          telefoneNumeroRef={telefoneNumeroRef}
          celularNumeroRef={celularNumeroRef}
          processoSeletivo={processoSeletivo!}
          instituicoesEnsino={instituicoesEnsino}
          comarcas={comarcas}
          distritos={distritos}
          cursoPretendidoId={cursoPretendidoId}
          comarcaPretendidaId={comarcaPretendidaId}
          pessoaComDeficiencia={pessoaComDeficiencia}
          optaVagasPcd={optaVagasPcd}
          declaraIndigena={declaraIndigena}
          raca={raca}
          instituicaoEnsinoIdStr={instituicaoEnsinoIdStr}
          comarcasDisponiveis={comarcasDisponiveis}
          distritosDisponiveis={distritosDisponiveis}
          municipios={municipiosFiltrados}
          onFileValidationError={(message) => toast.error(message)}
          handleViewFile={handleViewFile}
          handleDownloadFile={handleDownloadFile}
        />
        {/* <div className="mb-4">
          <h4 style={{ color: '#FF0000', justifyContent: 'center', display: 'flex' }}>
            AVISO
          </h4>
        </div>
        <div className="mb-4" style={{ color: '#FF0000 ', justifyContent: 'center', display: 'flex', maxWidth: '800px', margin: '0 auto' }}>
          <p>Para concluir o processo de inscrição, o candidato deverá efetuar o pagamento até o vencimento e enviar
            até o dia {new Date(processoSeletivo.dataVencimentoBoleto).toLocaleDateString('pt-BR')} no {processoSeletivo.nome}.</p>
        </div> */}
        <div className="mb-4">
          <div className="mb-3">
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI'} // Chave de teste do Google (sempre mostra desafio de imagens)
              size="normal" // Tamanho normal com checkbox "Não sou um robô"
              theme="light" // Tema claro
              onChange={(token) => setCaptchaToken(token)}
              onExpired={() => {
                setCaptchaToken(null);
                toast.warning('O captcha expirou. Por favor, complete a verificação novamente.');
              }}
              onError={() => {
                setCaptchaToken(null);
                toast.error('Erro ao carregar o captcha. Por favor, recarregue a página.');
              }}
            />
          </div>
          <button
            type="submit"
            className="btn btn-primary btn-lg"
            disabled={isLoading || !captchaToken}
          >
            {isLoading ? 'Enviando...' : 'Enviar Inscrição'}
          </button>
        </div>
      </form>
    </div>
  );
}

